import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowClockwise, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
  AreaChart, Area,
} from "recharts";
import api, { errMsg } from "../../lib/api";
import { unwrap, extractRows, extractTotal, epochParams, findCounts, findBuckets, formatIST } from "../../lib/helpers";
import { KVTable, DynamicTable } from "../../components/DataViews";
import { StatCard } from "../../components/StatCard";
import { DateRangePicker } from "../../components/DateRangePicker";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const FUNNEL = [
  { key: "sent", label: "Sent", color: "#002FA7", tone: "primary" },
  { key: "delivered", label: "Delivered", color: "#34C759", tone: "success" },
  { key: "read", label: "Read", color: "#0891B2", tone: "default" },
  { key: "failed", label: "Failed", color: "#FF3B30", tone: "error" },
  { key: "pending", label: "Pending", color: "#FFCC00", tone: "warning" },
];
const STATUS_KEYS = ["pending", "sent", "delivered", "read", "failed"];
const SERIES_COLORS = ["#002FA7", "#34C759", "#0891B2", "#FF3B30"];

function FunnelChart({ counts, testId }) {
  const chartData = FUNNEL.filter((f) => counts[f.key] !== undefined).map((f) => ({
    name: f.label, value: counts[f.key], color: f.color,
  }));
  if (!chartData.length) return null;
  return (
    <div className="border border-border rounded-md bg-card p-5" data-testid={testId}>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={chartData} layout="vertical" margin={{ left: 16, right: 24 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
          <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} stroke="hsl(var(--muted-foreground))" />
          <Tooltip cursor={{ fill: "hsl(var(--muted))" }} contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", color: "hsl(var(--foreground))" }} />
          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
            {chartData.map((d, i) => (
              <Cell key={i} fill={d.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function BucketChart({ buckets, testId }) {
  const first = buckets[0] || {};
  const xKey = Object.keys(first).find((k) => /date|day|bucket|_ts|time/i.test(k)) || Object.keys(first)[0];
  const series = Object.keys(first).filter((k) => k !== xKey && typeof first[k] === "number").slice(0, 4);
  if (!series.length) return null;
  const data = buckets.map((b) => ({
    ...b,
    [xKey]: typeof b[xKey] === "number" && b[xKey] > 1e9 ? formatIST(new Date(b[xKey] * 1000)) : b[xKey],
  }));
  return (
    <div className="border border-border rounded-md bg-card p-5" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-3">Last 7 days</div>
      <ResponsiveContainer width="100%" height={240}>
        <AreaChart data={data} margin={{ left: 0, right: 16 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis dataKey={xKey} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
          <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", color: "hsl(var(--foreground))" }} />
          {series.map((s, i) => (
            <Area key={s} type="monotone" dataKey={s} stroke={SERIES_COLORS[i % 4]} fill={SERIES_COLORS[i % 4]} fillOpacity={0.12} strokeWidth={2} />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ConversationsTab({ businessId, projectId }) {
  const [range, setRange] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failSource, setFailSource] = useState("chat");
  const [failPage, setFailPage] = useState(0);
  const [failData, setFailData] = useState(null);
  const [failLoading, setFailLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/dashboard/projects/analytics/conversations", {
        params: { wydely_business_id: businessId, wydely_project_id: projectId, ...epochParams(range) },
      });
      setData(unwrap(res.data));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [businessId, projectId, range]);

  const loadFailures = useCallback(async () => {
    setFailLoading(true);
    try {
      const res = await api.get("/dashboard/projects/analytics/conversations/failures", {
        params: { wydely_business_id: businessId, wydely_project_id: projectId, source: failSource, page: failPage, size: 25 },
      });
      setFailData(unwrap(res.data));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setFailLoading(false);
    }
  }, [businessId, projectId, failSource, failPage]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadFailures(); }, [loadFailures]);

  const chatCounts = useMemo(() => findCounts(data, STATUS_KEYS), [data]);
  const broadcastCounts = useMemo(() => findCounts(data?.broadcast, STATUS_KEYS), [data]);
  const buckets = useMemo(() => findBuckets(data), [data]);
  const failRows = extractRows(failData);
  const failTotal = extractTotal(failData, failRows.length);

  return (
    <div className="space-y-6" data-testid="conversations-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <DateRangePicker value={range} onChange={setRange} testId="conv-date-range" />
        <Button variant="outline" size="sm" onClick={() => { load(); loadFailures(); }} data-testid="conv-refresh">
          <ArrowClockwise className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28 w-full" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {FUNNEL.map((f) => (
              <StatCard
                key={f.key}
                label={f.label}
                value={chatCounts?.[f.key]?.toLocaleString() ?? "—"}
                tone={f.tone}
                testId={`conv-stat-${f.key}`}
              />
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {chatCounts && <FunnelChart counts={chatCounts} testId="conv-funnel-chart" />}
            {buckets && <BucketChart buckets={buckets} testId="conv-buckets-chart" />}
          </div>

          {broadcastCounts && (
            <section className="space-y-3">
              <h3 className="text-base font-display font-bold tracking-tight">Broadcast funnel</h3>
              <FunnelChart counts={broadcastCounts} testId="broadcast-funnel-chart" />
            </section>
          )}

          <details className="group">
            <summary className="text-xs uppercase tracking-widest text-muted-foreground cursor-pointer hover:text-foreground transition-colors" data-testid="conv-raw-toggle">
              Raw analytics payload
            </summary>
            <div className="mt-3">
              <KVTable data={data} testId="conv-raw-table" />
            </div>
          </details>
        </>
      )}

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-base font-display font-bold tracking-tight">Failure reasons</h3>
          <Select value={failSource} onValueChange={(v) => { setFailSource(v); setFailPage(0); }}>
            <SelectTrigger className="w-40" data-testid="failure-source-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="chat" data-testid="failure-source-chat">Chat</SelectItem>
              <SelectItem value="broadcast" data-testid="failure-source-broadcast">Broadcast</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {failLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <DynamicTable rows={failRows} testId="failures-table" empty="No failures recorded for this source." />
        )}
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground font-data" data-testid="failures-page-info">
            {failTotal} record{failTotal === 1 ? "" : "s"} · page {failPage + 1}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={failPage === 0 || failLoading} onClick={() => setFailPage((p) => p - 1)} data-testid="failures-prev">
              <CaretLeft className="mr-1 h-4 w-4" /> Prev
            </Button>
            <Button variant="outline" size="sm" disabled={failLoading || failRows.length < 25} onClick={() => setFailPage((p) => p + 1)} data-testid="failures-next">
              Next <CaretRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
